import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../constants/Colors';
import isMobile from '../../../../../components/IsMobile';
import { TemplateIssue } from '../../../../../constants/Types';

interface IssueDropdownProps {
  setIssueDropdownShown: Dispatch<SetStateAction<boolean>>,
  selectedIssueId: string | null,
  setSelectedIssueId: Dispatch<SetStateAction<string | null>>,
  issueDropdownShown: boolean,
  closeOtherDropdowns: () => void,
  hideLabel?: boolean,
  listPositionAbsolute?: boolean,
  issues: TemplateIssue[],
}

const IssueDropdown = ({
  setIssueDropdownShown, setSelectedIssueId, hideLabel, issues,
  selectedIssueId, issueDropdownShown, closeOtherDropdowns, listPositionAbsolute,
}: IssueDropdownProps) => {
  const [value, onChangeText] = useState<string>('');

  return <View
    style={styles.container}
    testID='filter-tickets-issue-list'
  >
    <View style={{ flexDirection: 'row' }}>
      <View style={styles.connectorContainer}>
        <View style={styles.issueConnector} />
        <View style={styles.connectorSpot} />
      </View>
      <View style={{ flex: 1 }}>
        {hideLabel ? <View style={{ marginBottom: styles.heading.marginBottom }} />
          : <Text style={styles.heading}>{'Issue'}</Text>}
        {issueDropdownShown ? <View style={styles.dropdownBox}>
          <TextInput
            placeholder='Search'
            style={[{ marginLeft: 8 }, !isMobile && { outlineWidth: 0 }]}
            placeholderTextColor={Colors.light.hereworksPrimaryGrey700}
            {...{ value, onChangeText }}
          />
          <TouchableOpacity
            style={{ flex: 1, alignItems: 'flex-end' }}
            onPress={() => {
              setIssueDropdownShown(false);
              closeOtherDropdowns();
            }}
          >
            <FontAwesome
              size={20}
              style={styles.icon}
              name={'angle-up'}
            />
          </TouchableOpacity>
        </View> : <TouchableOpacity
          style={styles.dropdownBox}
          onPress={() => {
            setIssueDropdownShown(!issueDropdownShown);
            closeOtherDropdowns();
          }}
        >
          <Text style={[
            styles.dropdownBoxText,
            !selectedIssueId && { color: Colors.light.hereworksPrimaryGrey800 },
          ]}>
            {issues.find((issue) => issue.Id === selectedIssueId)?.Text ?? 'None'}
          </Text>
          <View style={{ alignItems: 'flex-end' }}>
            <FontAwesome
              size={20}
              style={styles.icon}
              name={issueDropdownShown ? 'angle-up' : 'angle-down'}
            />
          </View>
        </TouchableOpacity>}
      </View>
    </View>
    <View style={styles.dropdownContainer}>
      {issueDropdownShown && <ScrollView
        nestedScrollEnabled
        indicatorStyle={'black'}
        style={[styles.dropdownView, listPositionAbsolute && styles.absoluteView]}
      >
        {issues.map((issue: TemplateIssue) => {
          const selected: boolean = issue?.Id === selectedIssueId;
          return (!(value?.length > 0)
            || issue?.Text.toLowerCase().includes(value?.trim().toLowerCase())) && <TouchableOpacity
              key={`issue_option_${issue?.Id}`}
              onPress={() => {
                setSelectedIssueId(issue?.Id ?? null);
                setIssueDropdownShown(false);
                closeOtherDropdowns();
              }}
              style={selected && styles.itemSelected}
            >
              <Text style={[styles.dropdownItemText, selected && styles.textSelected]}>
                {issue?.Text ?? 'ALL ISSUES'}
              </Text>
            </TouchableOpacity>;
        })}
      </ScrollView>}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    marginStart: 16,
  },
  dropdownBox: {
    width: '100%',
    height: 40,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  heading: {
    fontSize: 14,
    lineHeight: 18,
    marginTop: 12,
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  dropdownContainer: {
    marginStart: 20,
  },
  dropdownView: {
    zIndex: 5,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    backgroundColor: '#fff',
    marginTop: 4,
    maxHeight: 180,
    alignSelf: 'stretch',
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    marginVertical: 9.5,
    marginLeft: 12,
    flex: 1,
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
  textSelected: {
    color: '#FFF',
  },
  itemSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  icon: {
    color: Colors.light.hereworksBlue500,
    marginRight: 12,
  },
  connectorContainer: {
    flexDirection: 'row',
    marginBottom: 17,
    alignSelf: 'stretch',
  },
  issueConnector: {
    width: 16,
    marginBottom: 2,
    alignSelf: 'stretch',
    borderStartWidth: 1,
    borderBottomWidth: 1,
    borderBottomStartRadius: 6,
    borderColor: Colors.light.hereworksBlue100,
  },
  connectorSpot: {
    width: 3,
    height: 6,
    borderTopStartRadius: 3,
    borderBottomStartRadius: 3,
    backgroundColor: Colors.light.hereworksBlue100,
    alignSelf: 'flex-end',
  },
  absoluteView: {
    position: 'absolute',
    width: '100%',
  },
});

export default IssueDropdown;
